import { queryOptions, useQuery } from '@tanstack/react-query';

import {
  getCategories,
  getFeaturedProducts,
  getProduct,
  getStoreInfo,
  getTestimonials,
  listProducts,
} from '../api/catalog.api';
import { catalogKeys } from '../api/catalog.keys';
import { CATEGORY_PRESENTATION, presentationFor } from '../data/categories';
import { storeInfo as STORE_DEFAULTS } from '../data/store-info';

// The version poll handles live edits; this keeps plain navigations fresh too.
const CATALOG_STALE = 60_000;

export const productListOptions = (params = {}) =>
  queryOptions({
    queryKey: catalogKeys.productList(params),
    queryFn: ({ signal }) => listProducts(params, signal),
    staleTime: CATALOG_STALE,
  });

export function useProducts(params = {}) {
  return useQuery(productListOptions(params));
}

export function useFeaturedProducts() {
  return useQuery({
    queryKey: catalogKeys.featured(),
    queryFn: ({ signal }) => getFeaturedProducts(signal),
    staleTime: CATALOG_STALE,
  });
}

export function useProduct(slug) {
  return useQuery({
    queryKey: catalogKeys.product(slug),
    queryFn: ({ signal }) => getProduct(slug, signal),
    enabled: Boolean(slug),
    staleTime: CATALOG_STALE,
    retry: (failureCount, error) => error?.status !== 404 && failureCount < 2,
  });
}

/**
 * Every olfactive family the store actually stocks, dressed with curated copy
 * where we have it. Families we ship copy for lead (in that order) so the home
 * page reads as designed; anything else the admin adds follows, alphabetically
 * as the API returns it.
 */
export function useCategories() {
  return useQuery({
    queryKey: catalogKeys.categories(),
    queryFn: ({ signal }) => getCategories(signal),
    staleTime: CATALOG_STALE,
    select: (rows) => {
      const order = new Map(CATEGORY_PRESENTATION.map((c, i) => [c.family, i]));
      const rank = (family) => order.get(family) ?? order.size;

      return [...rows]
        .filter((row) => row.family)
        .sort((a, b) => rank(a.family) - rank(b.family))
        .map((row, i) => ({ ...presentationFor(row.family, i), count: row.count ?? 0 }));
    },
  });
}

export function useTestimonials() {
  return useQuery({
    queryKey: catalogKeys.testimonials(),
    queryFn: ({ signal }) => getTestimonials(signal),
    staleTime: CATALOG_STALE,
  });
}

/**
 * Live store info merged over the static defaults, so consumers always have a
 * complete object (shipping/COD/advance/bank) even before the request resolves.
 */
export function useStoreInfo() {
  return useQuery({
    queryKey: catalogKeys.storeInfo(),
    queryFn: ({ signal }) => getStoreInfo(signal),
    staleTime: CATALOG_STALE,
    placeholderData: STORE_DEFAULTS,
    select: (info) => ({ ...STORE_DEFAULTS, ...info }),
  });
}

/** Shipping charge for a subtotal given a store-info object (free above threshold). */
export function shippingFor(subtotal, info) {
  const threshold = info?.freeShippingThreshold ?? 0;
  return subtotal >= threshold ? 0 : (info?.shippingFee ?? 0);
}
